import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EllipsisVertical } from "lucide-react";

import "./EllipsisMenu.css";

const VIEWPORT_GAP = 8;
const MENU_GAP = 8;
const MAX_DROPDOWN_HEIGHT = 260;

export const EllipsisMenu = ({
  options = [],
  position = "right",
  disabled = false,
  ariaLabel = "Open options menu",
  buttonBackground = "transparent",
  buttonHoverBackground = "rgba(255, 255, 255, 0.08)",
  buttonActiveBackground = "rgba(255, 255, 255, 0.12)",
  iconColor = "#ffffff",
  dropdownBackground = "#181818",
  dropdownBorderColor = "#303030",
  itemHoverBackground = "rgba(255, 255, 255, 0.06)",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({
    visibility: "hidden",
  });

  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const menuOptions = Array.isArray(options) ? options : [];
  const isDisabled = disabled || menuOptions.length === 0;

  const themeStyle = {
    "--ellipsis-button-background": buttonBackground,
    "--ellipsis-button-hover-background": buttonHoverBackground,
    "--ellipsis-button-active-background": buttonActiveBackground,
    "--ellipsis-icon-color": iconColor,
    "--ellipsis-dropdown-background": dropdownBackground,
    "--ellipsis-dropdown-border-color": dropdownBorderColor,
    "--ellipsis-item-hover-background": itemHoverBackground,
  };

  const updatePosition = () => {
    const button = buttonRef.current;
    const dropdown = dropdownRef.current;

    if (!button || !dropdown) return;

    const rect = button.getBoundingClientRect();
    const width = dropdown.offsetWidth;
    const height = Math.min(dropdown.scrollHeight, MAX_DROPDOWN_HEIGHT);

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const openAbove = spaceBelow < height + MENU_GAP && spaceAbove > spaceBelow;

    const top = openAbove
      ? rect.top - height - MENU_GAP
      : rect.bottom + MENU_GAP;

    const left = position === "left" ? rect.left : rect.right - width;

    setDropdownStyle({
      position: "fixed",
      top: Math.max(
        VIEWPORT_GAP,
        Math.min(top, window.innerHeight - height - VIEWPORT_GAP),
      ),
      left: Math.max(
        VIEWPORT_GAP,
        Math.min(left, window.innerWidth - width - VIEWPORT_GAP),
      ),
      maxWidth: `calc(100vw - ${VIEWPORT_GAP * 2}px)`,
      maxHeight: MAX_DROPDOWN_HEIGHT,
      visibility: "visible",
      zIndex: 99999,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, position, menuOptions.length]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event) => {
      if (
        !buttonRef.current?.contains(event.target) &&
        !dropdownRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("pointerdown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("pointerdown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, position]);

  useEffect(() => {
    if (isDisabled) setIsOpen(false);
  }, [isDisabled]);

  const handleToggle = (event) => {
    event.stopPropagation();

    if (isDisabled) return;

    setDropdownStyle({ visibility: "hidden" });
    setIsOpen((current) => !current);
  };

  const handleSelect = (event, option) => {
    event.stopPropagation();

    if (option.disabled) return;

    setIsOpen(false);
    option.action?.(event);
  };

  const dropdown = (
    <div
      ref={dropdownRef}
      className={`ellipsis-menu__dropdown ellipsis-menu__dropdown--${position}`}
      style={{ ...dropdownStyle, ...themeStyle }}
      role="menu"
      aria-label={ariaLabel}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      {menuOptions.map((option, index) => (
        <button
          key={option.id || option.label || index}
          type="button"
          role="menuitem"
          className={["ellipsis-menu__item", option.className]
            .filter(Boolean)
            .join(" ")}
          disabled={option.disabled}
          onClick={(event) => handleSelect(event, option)}
        >
          {option.icon && (
            <span className="ellipsis-menu__item-icon">{option.icon}</span>
          )}

          <span className="ellipsis-menu__item-label">{option.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="ellipsis-menu" style={themeStyle}>
      <button
        ref={buttonRef}
        type="button"
        className={`ellipsis-menu__btn ${isOpen ? "is-active" : ""}`.trim()}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={isDisabled}
        onClick={handleToggle}
      >
        <EllipsisVertical size={18} aria-hidden="true" />
      </button>

      {isOpen && createPortal(dropdown, document.body)}
    </div>
  );
};

export default EllipsisMenu;
